import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserIdService } from '../../../../../core/auth';
import { GlobalMessageService, GlobalMessageType } from '../../../../../core/global-message';
import { B2BUserSearchParams, DataImport, UsersImportMode } from '../../../../../core/model';
import { UsersImportExportConnector } from '../connectors';

export interface UserImportError {
  message: string;
  reason: string;
  subject: string;
  subjectType: string;
  type: string;
}

@Injectable({
  providedIn: 'root',
})
export class UsersImportExportService {
  constructor(
    protected userIdService: UserIdService,
    protected usersImportExportConnector: UsersImportExportConnector,
    protected globalMessageService: GlobalMessageService
  ) {}

  getExportedUsersFile(searchParams: B2BUserSearchParams, totalCount: number): Observable<Blob> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.usersImportExportConnector.getExportedUsersFile(userId, searchParams, totalCount)),
      catchError(() => {
        this.handleExportError();
        return EMPTY;
      })
    );
  }

  sendImportUsersFile(file: File, mode: UsersImportMode): Observable<DataImport> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.usersImportExportConnector.sendImportUsersFile(userId, file, mode)),
      catchError((err) => {
        this.handleImportError();
        throw err;
      })
    );
  }

  downloadImportUsersFileTemplate(): Observable<Blob> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.usersImportExportConnector.downloadImportUsersFileTemplate(userId)),
      catchError(() => {
        this.handleImportFileTemplateError();
        return EMPTY;
      })
    );
  }

  protected handleImportFileTemplateError(): void {
    this.globalMessageService.add({ key: 'users.importFileTemplateDownloadFailure_message' }, GlobalMessageType.MSG_TYPE_ERROR);
  }

  protected handleExportError(): void {
    this.globalMessageService.add({ key: 'users.exportFailure_message' }, GlobalMessageType.MSG_TYPE_ERROR);
  }

  protected handleImportError(): void {
    this.globalMessageService.add({ key: 'users.importFailure_message' }, GlobalMessageType.MSG_TYPE_ERROR);
  }
}
